/**
 * Thème Material UI NORYVOX et logique de préférence d'affichage.
 *
 * Les valeurs proviennent du design system (`docs/07-ui-ux-design-system.md` § 3 et § 10)
 * et de sa référence d'implémentation (`docs/maquettes/assets/noryvox.css`).
 * Aucune couleur n'est inventée : les tokens ci-dessous réplient exactement le CSS.
 */

import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import type { ThemePreference } from '@noryvox/shared';

/** Mode effectif appliqué par Material UI (`system` est résolu en amont). */
export type ThemeMode = 'light' | 'dark';

/** Jeu de tokens d'un mode d'affichage. */
export interface DesignTokens {
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryContrast: string;
  accent: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  backgroundApp: string;
  backgroundSurface: string;
  backgroundSurfaceAlt: string;
  border: string;
  borderStrong: string;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
}

/** Tokens des deux modes (tableau § 10.1 du design system). */
export const designTokens: Record<ThemeMode, DesignTokens> = {
  light: {
    primary: '#4F46E5',
    primaryHover: '#4338CA',
    primaryActive: '#3730A3',
    primaryContrast: '#FFFFFF',
    accent: '#06B6D4',
    success: '#16A34A',
    warning: '#F59E0B',
    danger: '#DC2626',
    info: '#2563EB',
    backgroundApp: '#F9FAFB',
    backgroundSurface: '#FFFFFF',
    backgroundSurfaceAlt: '#F3F4F6',
    border: '#E5E7EB',
    borderStrong: '#D1D5DB',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    textDisabled: '#9CA3AF',
  },
  dark: {
    primary: '#818CF8',
    primaryHover: '#A5B4FC',
    primaryActive: '#C7D2FE',
    primaryContrast: '#0B0F19',
    accent: '#22D3EE',
    success: '#22C55E',
    warning: '#FBBF24',
    danger: '#F87171',
    info: '#60A5FA',
    backgroundApp: '#0B0F19',
    backgroundSurface: '#111827',
    backgroundSurfaceAlt: '#1F2937',
    border: '#2A3547',
    borderStrong: '#374151',
    textPrimary: '#F9FAFB',
    textSecondary: '#9CA3AF',
    textDisabled: '#6B7280',
  },
};

const FONT_FAMILY = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** Construit les options de thème MUI pour un mode donné. */
function buildThemeOptions(mode: ThemeMode): ThemeOptions {
  const tokens = designTokens[mode];
  const isLight = mode === 'light';

  return {
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        light: isLight ? '#6366F1' : '#A5B4FC',
        dark: isLight ? '#3730A3' : '#6366F1',
        contrastText: tokens.primaryContrast,
      },
      secondary: {
        main: tokens.accent,
        contrastText: isLight ? '#FFFFFF' : '#0B0F19',
      },
      success: { main: tokens.success },
      warning: { main: tokens.warning },
      error: { main: tokens.danger },
      info: { main: tokens.info },
      background: { default: tokens.backgroundApp, paper: tokens.backgroundSurface },
      divider: tokens.border,
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
        disabled: tokens.textDisabled,
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      h1: { fontSize: '1.875rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25 },
      h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25 },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.25 },
      h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25 },
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
      body1: { fontSize: '0.875rem', lineHeight: 1.45 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
      caption: { fontSize: '0.75rem', lineHeight: 1.45 },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
      },
      button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8, fontWeight: 600, textTransform: 'none' } },
      },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 12 } } },
      MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiAppBar: { defaultProps: { elevation: 0, color: 'default' } },
      MuiLink: { defaultProps: { underline: 'hover' } },
      MuiTooltip: { defaultProps: { arrow: true } },
    },
  };
}

/** Crée le thème MUI complet pour un mode d'affichage. */
export function createAppTheme(mode: ThemeMode): Theme {
  return createTheme(buildThemeOptions(mode));
}

/** Clé de stockage de la préférence d'affichage (alignée sur les maquettes). */
export const THEME_PREFERENCE_STORAGE_KEY = 'noryvox-theme';

/** Lit la préférence d'affichage persistée ; `system` par défaut. */
export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // Stockage indisponible : on retombe sur la préférence système.
  }
  return 'system';
}

/** Persiste la préférence d'affichage (sans effet bloquant en cas d'échec). */
export function persistThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    // Stockage indisponible : la préférence reste valable pour la session en cours.
  }
}

/** Résout la préférence (`light` / `dark` / `system`) en mode effectif. */
export function resolveThemeMode(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ThemeMode {
  if (preference === 'system') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return preference;
}
